// SSO Area lookup — Thai province (+ Bangkok district) → สำนักงานประกันสังคม branch.
//
// Canonical reference: /Desktop/CLAUDE/knowledge/sso_area/{README.md, sso_area.json}.
// Bangkok has 12 sub-area offices covering 50 districts. Most other provinces
// have exactly one provincial office, but a handful of large/peri-urban
// provinces operate additional branch sub-offices (สาขา) — see MULTI_BRANCH
// below. The dropdown lets the user pick any branch for that province, or add
// a new one (subset of the chosen province) when reality outpaces this table.
//
// Usage:
//   import { BANGKOK_DISTRICTS, ssoOptionsForProvince, isBangkok } from '@/lib/ssoArea'
//   const list = ssoOptionsForProvince(province)     // array of branch labels
//   const def  = resolveSsoBranch({ province, district }) // first option (back-compat default)

export const BANGKOK = 'กรุงเทพมหานคร'

/** 12 Bangkok sub-area offices, in numeric order. Each covers a cluster of khet (districts). */
export const BANGKOK_AREAS = [
  { code: 'BKK-01', short: 'สปส.พื้นที่ 1',  districts: ['พระนคร','ดุสิต','ป้อมปราบศัตรูพ่าย','สัมพันธวงศ์'] },
  { code: 'BKK-02', short: 'สปส.พื้นที่ 2',  districts: ['จตุจักร','ดอนเมือง','บางซื่อ','บางเขน','หลักสี่'] },
  { code: 'BKK-03', short: 'สปส.พื้นที่ 3',  districts: ['ดินแดง','พญาไท','ราชเทวี','ห้วยขวาง'] },
  { code: 'BKK-04', short: 'สปส.พื้นที่ 4',  districts: ['บางรัก','ปทุมวัน'] },
  { code: 'BKK-05', short: 'สปส.พื้นที่ 5',  districts: ['คลองสาน','ธนบุรี','บางกอกน้อย','บางกอกใหญ่','บางพลัด'] },
  { code: 'BKK-06', short: 'สปส.พื้นที่ 6',  districts: ['ตลิ่งชัน','ทวีวัฒนา','บางแค','ภาษีเจริญ','หนองแขม'] },
  { code: 'BKK-07', short: 'สปส.พื้นที่ 7',  districts: ['จอมทอง','ทุ่งครุ','บางขุนเทียน','บางบอน','ราษฎร์บูรณะ'] },
  { code: 'BKK-08', short: 'สปส.พื้นที่ 8',  districts: ['บางนา','ประเวศ','พระโขนง','สวนหลวง'] },
  { code: 'BKK-09', short: 'สปส.พื้นที่ 9',  districts: ['คันนายาว','บางกะปิ','ลาดพร้าว','วังทองหลาง','บึงกุ่ม'] },
  { code: 'BKK-10', short: 'สปส.พื้นที่ 10', districts: ['คลองสามวา','มีนบุรี','ลาดกระบัง','สะพานสูง','หนองจอก','สายไหม'] },
  { code: 'BKK-11', short: 'สปส.พื้นที่ 11', districts: ['ยานนาวา','สาทร','บางคอแหลม'] },
  { code: 'BKK-12', short: 'สปส.พื้นที่ 12', districts: ['วัฒนา','คลองเตย'] },
]

/** Flat, alphabetically-sorted list of all 50 Bangkok districts — feed straight into a <Combobox>. */
export const BANGKOK_DISTRICTS = [...new Set(BANGKOK_AREAS.flatMap(a => a.districts))].sort((a, b) => a.localeCompare(b, 'th'))

// Internal lookup: district → office short name
const BKK_DISTRICT_TO_BRANCH = Object.fromEntries(
  BANGKOK_AREAS.flatMap(a => a.districts.map(d => [d, a.short]))
)

export function isBangkok(province) {
  return province === BANGKOK
}

export function isBangkokDistrict(district) {
  return !!district && district in BKK_DISTRICT_TO_BRANCH
}

// Provinces with multiple SSO offices (provincial HQ + branch sub-offices).
// Source: wiki/healthserv references — see knowledge/sso_area/README.md.
// First entry in each list is the provincial HQ (อ.เมือง).
const MULTI_BRANCH = {
  'สมุทรปราการ': ['ปกส. สมุทรปราการ (อ.เมือง)', 'ปกส. สมุทรปราการ สาขาบางพลี', 'ปกส. สมุทรปราการ สาขาพระประแดง'],
  'สมุทรสาคร':  ['ปกส. สมุทรสาคร (อ.เมือง)',  'ปกส. สมุทรสาคร สาขากระทุ่มแบน'],
  'นนทบุรี':    ['ปกส. นนทบุรี (อ.เมือง)',    'ปกส. นนทบุรี สาขาบางบัวทอง'],
  'ปทุมธานี':   ['ปกส. ปทุมธานี (อ.เมือง)',   'ปกส. ปทุมธานี สาขาคลองหลวง'],
  'เชียงใหม่':  ['ปกส. เชียงใหม่ (อ.เมือง)',  'ปกส. เชียงใหม่ สาขาฝาง'],
  'สงขลา':      ['ปกส. สงขลา (อ.เมือง)',      'ปกส. สงขลา สาขาหาดใหญ่'],
  'ลพบุรี':     ['ปกส. ลพบุรี (อ.เมือง)',     'ปกส. ลพบุรี สาขาชัยบาดาล'],
  'สระแก้ว':    ['ปกส. สระแก้ว (อ.เมือง)',    'ปกส. สระแก้ว สาขาอรัญประเทศ'],
}

// User-added branches live in localStorage under this key (a map of
// province → string[]). Editing happens client-side; we expose helpers so the
// UI doesn't poke at localStorage directly.
const CUSTOM_KEY = 'tt.ssoCustomBranches.v1'

function readCustomMap() {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch { return {} }
}
function writeCustomMap(map) {
  try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(map)) } catch { /* quota */ }
}

/**
 * Branches the user has manually added for a province (stored in localStorage).
 */
export function customBranchesFor(province) {
  if (!province) return []
  const map = readCustomMap()
  const list = Array.isArray(map[province]) ? map[province] : []
  return list.slice()
}

/**
 * Add `branch` to the user-saved list for `province`. Returns the updated list.
 * No-op if the branch is empty or already present (case/whitespace-insensitive).
 */
export function addCustomBranch(province, branch) {
  const p = (province || '').trim()
  const b = (branch || '').trim().slice(0, 200)
  if (!p || !b) return customBranchesFor(p)
  const map = readCustomMap()
  const cur = Array.isArray(map[p]) ? map[p] : []
  const seen = new Set([...(MULTI_BRANCH[p] || []), ...cur].map((s) => s.trim().toLowerCase()))
  if (seen.has(b.toLowerCase())) return cur.slice()
  const next = [...cur, b]
  map[p] = next
  writeCustomMap(map)
  return next.slice()
}

/**
 * Remove a user-added branch. Built-in (MULTI_BRANCH / generated) branches
 * are not removable.
 */
export function removeCustomBranch(province, branch) {
  const p = (province || '').trim()
  if (!p) return []
  const map = readCustomMap()
  const cur = Array.isArray(map[p]) ? map[p] : []
  const next = cur.filter((s) => s !== branch)
  if (next.length === cur.length) return cur.slice()
  if (next.length) map[p] = next
  else             delete map[p]
  writeCustomMap(map)
  return next.slice()
}

/**
 * All available ประกันสังคมเขต options for a province (built-in + user-added).
 * Bangkok is excluded — its branch is derived from the district, so the UI
 * keeps its existing district-picker flow.
 */
export function ssoOptionsForProvince(province) {
  if (!province || isBangkok(province)) return []
  const builtin = MULTI_BRANCH[province] || [`ปกส. ${province}`]
  const custom  = customBranchesFor(province)
  return [...builtin, ...custom]
}

/**
 * Back-compat default resolver — returns the FIRST option (provincial HQ for
 * multi-branch provinces). Callers that already pre-fill an SSO field on
 * province change use this so the existing behavior doesn't break.
 */
export function resolveSsoBranch({ province, district } = {}) {
  if (!province) return ''
  if (isBangkok(province)) {
    if (!district) return ''
    return BKK_DISTRICT_TO_BRANCH[district] || ''
  }
  return ssoOptionsForProvince(province)[0] || ''
}
